import { useState, useEffect } from "react";
import { InAppTemplate } from "../../../shared/templates/inapp";
import { BlogService } from "../../../services/blogService";
import { BlogCategoryDto } from "../../../types/app/blogTypes";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import DeleteIco from "../../../assets/icons/delete.svg";
import EditIco from "../../../assets/icons/edit.svg";
import { CategoriesContent } from "./components/CategoriesContent";
import "./blog.scss";
import styles from "./categories.module.css";

const CategorySchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Category name is required"),
  description: Yup.string().max(200, "Description is too long"),
});

const BlogCategories = () => {
  const [categories, setCategories] = useState<BlogCategoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchSlug, setSearchSlug] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<BlogCategoryDto | null>(null);

  const [saving, setSaving] = useState(false);
  const [operationSuccess, setOperationSuccess] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await BlogService.getBlogCategories(true);
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to load categories. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchBySlug = async () => {
    if (!searchSlug.trim()) {
      loadCategories();
      return;
    }

    try {
      setLoading(true);
      setIsSearching(true);
      setError(null);
      const category = await BlogService.getBlogCategoryBySlug(searchSlug);
      setCategories(category ? [category] : []);
    } catch (err) {
      setError("Category not found with this slug.");
      setCategories([]);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const handleCreate = async (values: any, { resetForm }: any) => {
    try {
      setSaving(true);
      await BlogService.createBlogCategory({
        name: values?.name || "",
        description: values?.description || "",
      });

      setOperationSuccess("Category created successfully!");
      setShowAddModal(false);
      resetForm();
      loadCategories();

      setTimeout(() => setOperationSuccess(null), 3000);
    } catch (error) {
      setErrorMessage("Failed to create category. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (values: any) => {
    if (!selectedCategory) return;

    try {
      setSaving(true);
      await BlogService.updateBlogCategory(selectedCategory.id, {
        id: selectedCategory.id,
        name: values?.name || selectedCategory.name,
        description: values?.description || "",
      });

      setOperationSuccess("Category updated successfully!");
      setShowEditModal(false);
      setSelectedCategory(null);
      loadCategories();

      setTimeout(() => setOperationSuccess(null), 3000);
    } catch (error) {
      setErrorMessage("Failed to update category. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;

    try {
      setSaving(true);
      await BlogService.deleteBlogCategory(selectedCategory.id);

      setOperationSuccess("Category deleted successfully!");
      setShowDeleteModal(false);
      setSelectedCategory(null);
      loadCategories();

      setTimeout(() => setOperationSuccess(null), 3000);
    } catch (error) {
      setErrorMessage("Failed to delete category. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const categoryTable = () => {
    return (
      <>
        <thead>
          <tr>
            <th className="left_top">
              <span>Name</span>
            </th>
            <th>Slug</th>
            <th>Description</th>
            <th>Posts</th>
            <th>Created</th>
            <th>Updated</th>
            <th className="right_top blanked">
              <span></span>
            </th>
          </tr>
        </thead>
        <tbody>
          {categories.length === 0 ? (
            <tr>
              <td colSpan={6} className={styles.emptyCell}>
                No categories found. Create your first category!
              </td>
            </tr>
          ) : (
            categories.map((category) => (
              <tr key={category.id}>
                <td>
                  <div className="category-name">
                    <strong>{category?.name || "Unnamed"}</strong>
                  </div>
                </td>
                <td className="grayed">{category?.slug || "N/A"}</td>
                <td className="grayed">
                  {category?.description || "No description"}
                </td>
                <td className="branded">{category?.postCount ?? 0}</td>
                <td className="grayed">
                  {category?.createdAt
                    ? new Date(category.createdAt).toLocaleDateString()
                    : "N/A"}
                </td>
                <td className="grayed">
                  {category?.updatedAt
                    ? new Date(category.updatedAt).toLocaleDateString()
                    : "-"}
                </td>
                <td>
                  <div className="action_ctas">
                    <button
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowEditModal(true);
                      }}
                    >
                      <img src={EditIco} alt="Edit" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowDeleteModal(true);
                      }}
                    >
                      <img src={DeleteIco} alt="Delete" />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </>
    );
  };

  const categoryForm = ({ initialValues, onSubmit, isEdit = false }: any) => (
    <Formik
      initialValues={initialValues}
      validationSchema={CategorySchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ errors, touched }) => (
        <Form className="modal-form">
          <label>Category name</label>
          <Field
            name="name"
            type="text"
            className="input-field"
            placeholder="Enter category name"
          />
          <ErrorMessage name="name" component="div" className="error-text" />

          <label>Description</label>
          <Field
            name="description"
            as="textarea"
            rows={3}
            className="textarea-field"
            placeholder="Brief description of this category..."
          />
          <span className="helper-text">Maximum of 200 characters</span>
          <ErrorMessage
            name="description"
            component="div"
            className="error-text"
          />

          <button type="submit" className="submit-btn" disabled={saving}>
            {saving
              ? "Saving..."
              : isEdit
              ? "Update category"
              : "Add new category"}
          </button>
        </Form>
      )}
    </Formik>
  );

  return (
    <InAppTemplate
      pageHeadingTitle="Blog Categories"
      childComponent={
        <CategoriesContent
          loading={loading}
          error={error}
          categories={categories}
          operationSuccess={operationSuccess}
          searchSlug={searchSlug}
          isSearching={isSearching}
          showAddModal={showAddModal}
          showEditModal={showEditModal}
          showDeleteModal={showDeleteModal}
          selectedCategory={selectedCategory}
          saving={saving}
          errorMessage={errorMessage}
          onRetry={loadCategories}
          onSearchChange={setSearchSlug}
          onSearch={handleSearchBySlug}
          onClearSearch={() => {
            setSearchSlug("");
            loadCategories();
          }}
          onShowAddModal={() => setShowAddModal(true)}
          onShowEditModal={(category: BlogCategoryDto) => {
            setSelectedCategory(category);
            setShowEditModal(true);
          }}
          onShowDeleteModal={(category: BlogCategoryDto) => {
            setSelectedCategory(category);
            setShowDeleteModal(true);
          }}
          onHideAddModal={() => setShowAddModal(false)}
          onHideEditModal={() => setShowEditModal(false)}
          onHideDeleteModal={() => setShowDeleteModal(false)}
          onHideErrorModal={() => setErrorMessage("")}
          onCreateCategory={handleCreate}
          onUpdateCategory={handleUpdate}
          onDeleteCategory={handleDelete}
          CategoryForm={categoryForm}
          CategoryTable={categoryTable}
        />
      }
    />
  );
};

export default BlogCategories;
